import { ref, watch, onUnmounted } from 'vue';

/**
 * Composable for auto-refresh functionality with countdown timer
 * @param {Function} callback - Function to call on each refresh
 * @param {Number} intervalSeconds - Interval in seconds (default: 30)
 * @returns {Object} - Auto-refresh state and controls
 */
export const useAutoRefresh = (callback, intervalSeconds = 30) => {
  const enabled = ref(true);
  const countdown = ref(intervalSeconds);

  let refreshInterval = null;
  let countdownInterval = null;

  /**
   * Start auto-refresh and countdown
   */
  const start = () => {
    stop(); // Clear any existing intervals

    if (enabled.value) {
      // Refresh interval
      refreshInterval = setInterval(() => {
        callback();
        resetCountdown();
      }, intervalSeconds * 1000);

      // Countdown interval
      countdownInterval = setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          countdown.value = intervalSeconds;
        }
      }, 1000);
    }
  };

  /**
   * Stop auto-refresh and countdown
   */
  const stop = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
  };

  /**
   * Reset countdown to initial value
   */
  const resetCountdown = () => {
    countdown.value = intervalSeconds;
  };

  /**
   * Toggle auto-refresh on/off
   */
  const toggle = () => {
    enabled.value = !enabled.value;
  };

  // Watch for enabled changes
  watch(enabled, (newValue) => {
    if (newValue) {
      start();
    } else {
      stop();
    }
  });

  // Cleanup on unmount
  onUnmounted(() => {
    stop();
  });

  return {
    enabled,
    countdown,
    start,
    stop,
    toggle,
    resetCountdown,
  };
};
